import {
    createContext,
    useCallback,
    useContext,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import type { BoardEnteringReveal } from "./Board.types";
import type { BoardEntrySlot } from "./BoardCellList";
import { readBoardMotion, type BoardMotion } from "./boardMotion";
import { BOARD_CARD_GAP } from "./boardWindowing";

const FADE_IN: Keyframe[] = [{ opacity: 0 }, { opacity: 1 }];
const CARD_HIDDEN: Keyframe = {
    opacity: 0,
    translate: "0 4px",
    scale: "0.98",
};
const CARD_SHOWN: Keyframe = { opacity: 1, translate: "0 0", scale: "1" };

// Group drops land their cards one after another, capped so a big group
// still lands within a beat.
const LANDING_STAGGER_MS = 30;
const LANDING_STAGGER_LIMIT = 5;

const getTiming = (motion: BoardMotion) => ({
    duration: motion.durationMs,
    easing: motion.easing,
});

/**
 * A created card in a windowed cell fades in from slightly low and small,
 * in a slot already at full height: the window has scrolled to the slot it
 * measured, and a growing slot would end past that scroll. A card dropped
 * out of view enters the same way once its column has scrolled to it.
 * A delayed card stays hidden until it starts.
 */
export const playCardFadeIn = (
    slot: HTMLElement,
    motion: BoardMotion,
    delayMs = 0,
) =>
    slot.animate(motion.isReduced ? FADE_IN : [CARD_HIDDEN, CARD_SHOWN], {
        ...getTiming(motion),
        delay: delayMs,
        fill: "backwards",
    });

/**
 * Ref for a card's `li` that a group drop just moved (`landingIndex`, its
 * place among them): it enters where it landed, a beat after the one
 * before. Reduced motion fades them in together.
 */
export const useBoardCardLanding = (landingIndex: number | undefined) => {
    const slotRef = useRef<HTMLElement | null>(null);
    useLayoutEffect(() => {
        const node = slotRef.current;
        if (
            landingIndex === undefined ||
            node === null ||
            typeof node.animate !== "function"
        ) {
            return;
        }
        const motion = readBoardMotion();
        const step = Math.min(landingIndex, LANDING_STAGGER_LIMIT);
        playCardFadeIn(
            node,
            motion,
            motion.isReduced ? 0 : step * LANDING_STAGGER_MS,
        );
    }, [landingIndex]);
    return slotRef;
};

/**
 * A created card in a plain cell fades in the same way while its slot opens
 * from zero height, so the cards below slide down instead of jumping. The
 * slot clips the card while it opens. Reduced motion keeps the fade only.
 * The height is animated on purpose: one short reveal of one slot, where
 * scaling would distort the card and leave the cards below jumping.
 */
const playSlotOpening = (slot: HTMLElement, motion: BoardMotion) => {
    if (motion.isReduced) {
        return slot.animate(FADE_IN, getTiming(motion));
    }
    const hasSiblings =
        slot.previousElementSibling !== null ||
        slot.nextElementSibling !== null;
    const { overflow } = slot.style;
    slot.style.overflow = "clip";
    const animation = slot.animate(
        [
            {
                ...CARD_HIDDEN,
                // react-doctor-disable-next-line react-doctor/no-layout-property-animation
                height: "0px",
                // The list's flex gap, which the slot only takes up as it opens.
                marginBlockStart: hasSiblings ? `${-BOARD_CARD_GAP}px` : "0px",
            },
            {
                ...CARD_SHOWN,
                // react-doctor-disable-next-line react-doctor/no-layout-property-animation
                height: `${slot.offsetHeight}px`,
                marginBlockStart: "0px",
            },
        ],
        getTiming(motion),
    );
    const restoreOverflow = () => {
        slot.style.overflow = overflow;
    };
    animation.addEventListener("finish", restoreOverflow);
    animation.addEventListener("cancel", restoreOverflow);
    return animation;
};

/**
 * Items the surface just created (`enteringItemIds`). Each one's cards
 * animate in the first time they mount. Nothing animates in the board's
 * first render, and ids passed by then count as shown. An id dropped from
 * the list forgets it was shown, so passing it again makes it enter again.
 */
export interface BoardEntering {
    /** Created items whose cards haven't entered yet. */
    pendingIds: ReadonlySet<string>;
    markShown: (itemId: string) => void;
    reveal: BoardEnteringReveal;
}

export const BoardEnteringContext = createContext<BoardEntering | null>(null);

const NO_IDS: readonly string[] = [];

export const useBoardEntering = (
    enteringItemIds: readonly string[] = NO_IDS,
    reveal: BoardEnteringReveal = "scroll",
): BoardEntering => {
    const [shownIds, setShownIds] = useState<ReadonlySet<string>>(
        () => new Set(enteringItemIds),
    );
    const [prunedFor, setPrunedFor] = useState(enteringItemIds);
    if (prunedFor !== enteringItemIds) {
        setPrunedFor(enteringItemIds);
        const keptIds = enteringItemIds.filter((itemId) =>
            shownIds.has(itemId),
        );
        if (keptIds.length !== shownIds.size) {
            setShownIds(new Set(keptIds));
        }
    }
    const markShown = useCallback(
        (itemId: string) =>
            setShownIds((previous) =>
                previous.has(itemId) ? previous : new Set(previous).add(itemId),
            ),
        [],
    );
    return useMemo(
        () => ({
            pendingIds: new Set(
                enteringItemIds.filter((itemId) => !shownIds.has(itemId)),
            ),
            markShown,
            reveal,
        }),
        [enteringItemIds, shownIds, markShown, reveal],
    );
};

/**
 * Ref for a card's `li`. A pending item's card animates in on mount and,
 * unless `reveal` is `"none"`, scrolls into view (nearest edge, both axes,
 * so sticky headers' scroll padding is respected); in a plain cell its slot
 * opens too. A windowed cell mounts it in a measured slot (scrolling to its
 * index first when it was outside the window and `reveal` is `"scroll"`),
 * so its slot keeps its full height. Decided once, at
 * mount: a card remounting (filters, regrouping, windowing) never animates,
 * and an id passed after its card mounted is marked shown without animating.
 */
export const useBoardCardEntrance = (itemId: string, slot: BoardEntrySlot) => {
    const entering = useContext(BoardEnteringContext);
    const enteringRef = useRef(entering);
    const slotRef = useRef<HTMLElement | null>(null);
    const hasMountedRef = useRef(false);
    const isWindowed = slot.measureRef !== undefined;
    // Runs before the mount effect below, so on the mount commit it leaves a
    // pending id for that effect to animate.
    useLayoutEffect(() => {
        enteringRef.current = entering;
        if (hasMountedRef.current && entering?.pendingIds.has(itemId)) {
            entering.markShown(itemId);
        }
    }, [entering, itemId]);

    useLayoutEffect(() => {
        const node = slotRef.current;
        const current = enteringRef.current;
        hasMountedRef.current = true;
        if (
            node === null ||
            current === null ||
            !current.pendingIds.has(itemId)
        ) {
            return;
        }
        current.markShown(itemId);
        const motion = readBoardMotion();
        if (current.reveal === "scroll") {
            node.scrollIntoView({
                block: "nearest",
                inline: "nearest",
                behavior: motion.isReduced || isWindowed ? "auto" : "smooth",
            });
        }
        if (typeof node.animate === "function") {
            const play = isWindowed ? playCardFadeIn : playSlotOpening;
            play(node, motion);
        }
    }, [itemId, isWindowed]);

    return slotRef;
};
